import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Player } from '@schibsted-svp/web-player';
import cn from 'classnames';
import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { getAssetMetadata } from '../../../../utils/player/asset';
import { Backdrop } from '../../components/Backdrop/Backdrop';
import { AirPlayButton } from '../../components/buttons/AirPlayButton';
import { CastButton } from '../../components/buttons/CastButton';
import { FullscreenButton } from '../../components/buttons/FullscreenButton';
import { PipButton } from '../../components/buttons/PipButton';
import { SettingsButton } from '../../components/buttons/SettingsButton';
import { Captions } from '../../components/Captions/Captions';
import { DoubleTap } from '../../components/DoubleTap/DoubleTap';
import { NextUpSlider } from '../../components/NextUpSlider/NextUpSlider';
import { Overlay } from '../../components/Overlay/Overlay';
import { PlaybackButtons } from '../../components/PlaybackButtons/PlaybackButtons';
import { ProgressBar } from '../../components/ProgressBar/ProgressBar';
import { PromotionPicture } from '../../components/PromotionPicture/PromotionPicture';
import { Settings } from '../../components/Settings/Settings';
import { TimeDisplay } from '../../components/TimeDisplay/TimeDisplay';
import { VolumeButton } from '../../components/VolumeButton/VolumeButton';
import { useCast } from '../../hooks/CastProvider';
import { useConfig } from '../../hooks/ConfigProvider';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { useNextAsset } from '../../hooks/NextAssetProvider/NextAssetProvider';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useSeries } from '../../hooks/SeriesProvider';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { usePlayerInteraction } from '../../hooks/usePlayerInteraction';
import { usePlayerMute } from '../../hooks/usePlayerMute';
import { usePlayerState } from '../../hooks/usePlayerState';
import { usePlayNext } from '../../hooks/usePlayNext';
import { useStreamType } from '../../hooks/useStreamType';
import { useTracker } from '../../hooks/useTracker';
import { useViewportType } from '../../hooks/useViewportType';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';
import {
  hasEditorialLink,
  hasSponsorData,
  isEpisode,
} from '../../utils/assetHelpers';
import { convertTimestampToSeconds, matchesTimeRegex } from '../../utils/time';
import { Quality } from '../../components/Quality/Quality';
import { IconButton } from '../../components/IconButton/IconButton';
import { useCaptions } from '../../hooks/useCaptions';
import { useProvider } from '../../hooks/useProvider';
import { useQualityLevels } from '../../hooks/useQualityLevels';
import { getSingleAvailableOption } from './helpers';

export type OverlayType = 'captions' | 'settings' | 'next-up' | 'quality';

export const DefaultLayout: FunctionComponent = () => {
  const layoutRef = useRef<HTMLDivElement>(null!);
  const playerStateRef = useRef<ReturnType<Player['getState']>>();
  const state = usePlayerState();
  const player = usePlayer();
  const { trackEngagementUIElement, trackImpressionUIElement } =
    useTpaasTracker();
  const { playNext } = usePlayNext();
  const { context } = useConfig();
  const { canSeek } = useStreamType();
  const { isActive: isCasting } = useCast();
  const { asset } = useCurrentAsset();
  const { seasons } = useSeries();
  const { isMobile } = useViewportType();
  const { nextAsset } = useNextAsset();
  const { isMuted } = usePlayerMute();
  const [openOverlayName, setOpenOverlayName] = useState<OverlayType>();
  const [shouldShowPromoImage, setShouldShowPromoImage] = useState(false);
  const { trackButtonClick, trackNextUpClick } = useTracker();
  const {
    promo_image_timestamp: promoImageTimestamp,
    promo_image_url: promoImage,
  } = getAssetMetadata(asset);

  const isOverlayOpen = Boolean(openOverlayName);
  const hasSeasons = Boolean(seasons.length);
  const displayNextUp = Boolean(
    nextAsset && asset && (!isEpisode(asset) || hasSeasons)
  );
  const { configProvider } = useProvider();
  const { getVariant } = useConfig();

  const { captionsList } = useCaptions();
  const hasCaptions = captionsList.length > 1;

  const canReportIssue =
    getVariant('error-reporting') === 'enabled' && configProvider === 'vgtv';

  const { hasQualities } = useQualityLevels();

  const canDisplayQualities = !isCasting && hasQualities;
  const hasSettingsOptions =
    displayNextUp || hasCaptions || canReportIssue || canDisplayQualities;

  const singleAvailableOption = getSingleAvailableOption({
    displayNextUp,
    hasCaptions,
    canReportIssue,
    canDisplayQualities,
  });

  const handleOverlayCloseClick = useCallback(() => {
    setOpenOverlayName(undefined);
  }, []);

  const handleTime = useCallback(
    (param: jwplayer.TimeParam) => {
      if (!promoImage || !promoImageTimestamp) {
        return;
      }
      if (!matchesTimeRegex(promoImageTimestamp)) {
        return;
      }

      const timestampInSeconds = convertTimestampToSeconds(promoImageTimestamp);

      if (
        Math.round(param.position) === timestampInSeconds &&
        !shouldShowPromoImage
      ) {
        setShouldShowPromoImage(true);
      }
    },
    [promoImage, promoImageTimestamp, shouldShowPromoImage]
  );

  const handleCaptionsButtonClick = useCallback(() => {
    setOpenOverlayName('captions');
    trackButtonClick('captions');
  }, [trackButtonClick]);

  const handleSettingsButtonClick = useCallback(() => {
    if (singleAvailableOption === 'captions') {
      setOpenOverlayName('captions');
      trackButtonClick('captions');
      return;
    }

    if (singleAvailableOption === 'next-up') {
      setOpenOverlayName('next-up');
      trackButtonClick('next-up');
      return;
    }

    if (singleAvailableOption === 'quality') {
      setOpenOverlayName('quality');
      trackButtonClick('quality');
      return;
    }

    setOpenOverlayName('settings');
    trackButtonClick('settings');
  }, [singleAvailableOption, trackButtonClick]);

  const handleNextUpButtonClick = useCallback(() => {
    setOpenOverlayName('next-up');
    trackButtonClick('next-up');
  }, [trackButtonClick]);

  const handleQualityButtonClick = useCallback(() => {
    setOpenOverlayName('quality');
    trackButtonClick('quality');
  }, [trackButtonClick]);

  useEffect(() => {
    const jw = player.getJWPlayer();
    if (!jw) {
      return;
    }

    if (isOverlayOpen) {
      playerStateRef.current = jw.getState();

      player.pause();

      trackImpressionUIElement({
        elementType: 'div',
        foldPosition: 'Above',
        name: 'NextUp',
      });
    } else if (playerStateRef.current && playerStateRef.current !== 'paused') {
      player.play();

      playerStateRef.current = undefined;
    }
  }, [isOverlayOpen, player]);

  useJWPlayerEvent('play', handleOverlayCloseClick);
  useJWPlayerEvent('time', handleTime);

  const handleNextUpAssetClick = useCallback(
    (newAsset: Asset, e: MouseEvent) => {
      playNext(newAsset, {
        pulse: { playbackSource: 'manualNext-related' },
      });
      trackNextUpClick(newAsset.id);
      if (e.currentTarget instanceof Element) {
        trackEngagementUIElement({
          elementName: 'PlayNext',
          elementText: e.currentTarget.textContent ?? undefined,
          elementType: e.currentTarget.tagName.toLowerCase(),
          name: 'PlayNext',
          playEntry: 'Next',
        });
      }
      setOpenOverlayName(undefined);
    },
    [playNext, trackNextUpClick, trackEngagementUIElement]
  );

  const {
    isActive,
    handleLeftDoubleTap,
    handleRightDoubleTap,
    handleProgressBarInteraction,
    handleTap,
  } = usePlayerInteraction({
    layoutRef,
  });

  const isVisible =
    isActive ||
    isCasting ||
    state === 'paused' ||
    state === 'buffering' ||
    state === 'complete';

  useEffect(() => {
    if (isVisible || isMuted) {
      layoutRef.current.style.cursor = 'initial';
    } else {
      layoutRef.current.style.cursor = 'none';
    }
  }, [isVisible, isMuted]);

  const promoImageSupported = promoImageTimestamp && promoImage;
  const isLinkAvailable =
    asset && (hasSponsorData(asset) || hasEditorialLink(asset));

  return (
    <div ref={layoutRef} className="vgp-layout">
      {promoImageSupported && (
        <div
          className={cn('vgp-layout-promotion-picture', {
            'vgp-layout-promotion-picture-hidden': !shouldShowPromoImage,
          })}
        >
          <PromotionPicture promoImageUrl={promoImage} />
        </div>
      )}
      <DoubleTap
        onTap={handleTap}
        onLeftDoubleTap={handleLeftDoubleTap}
        onRightDoubleTap={handleRightDoubleTap}
        collapsed={isVisible}
      />
      {(isLinkAvailable || isVisible) && (
        <div
          className={cn('vgp-layout-playback-buttons', {
            'vgp-layout-playback-buttons--hidden':
              !isVisible && !isLinkAvailable,
          })}
        >
          <PlaybackButtons isVisible={isVisible} />
          <div
            className={cn('vgp-time-display-container', {
              'vgp-time-display-container--hidden': !isVisible,
            })}
          >
            <TimeDisplay />
          </div>
        </div>
      )}
      <Backdrop isVisible={isVisible} className="vgp-layout-backdrop">
        <div className="vgp-layout-container">
          <div className="vgp-layout-top">
            <div className="vgp-buttons vgp-layout-top-buttons">
              <CastButton color="video-ui" />
              <AirPlayButton color="video-ui" />
              <PipButton color="video-ui" />
              <FullscreenButton color="video-ui" />
            </div>
          </div>
          <div className="vgp-layout-bottom">
            <div className="vgp-layout-right-side-buttons">
              <IconButton color="video-ui">
                <VolumeButton className="vgp-volume-button" />
              </IconButton>
              {hasSettingsOptions && (
                <div className="vgp-right-side-group-buttons">
                  <SettingsButton
                    onClick={handleSettingsButtonClick}
                    color="video-ui"
                  />
                </div>
              )}
            </div>

            <div className="vgp-layout-progress-bar-container">
              {canSeek && (
                <ProgressBar onInteraction={handleProgressBarInteraction} />
              )}
            </div>
          </div>
        </div>
        {openOverlayName === 'settings' && (
          <Overlay
            onCloseClick={handleOverlayCloseClick}
            className="vgp-overlay-settings"
            disableCloseButton
            contentOverlayClassName="vgp-overlay-settings-content"
          >
            <Settings
              displayNextUp={displayNextUp}
              canReportIssue={canReportIssue}
              hasCaptions={hasCaptions}
              handleNextUpButtonClick={handleNextUpButtonClick}
              handleCaptionsButtonClick={handleCaptionsButtonClick}
              handleQualityButtonClick={handleQualityButtonClick}
              canDisplayQualities={canDisplayQualities}
            />
          </Overlay>
        )}
        {openOverlayName === 'captions' && (
          <Overlay
            onCloseClick={handleOverlayCloseClick}
            className="vgp-overlay-settings"
            contentOverlayClassName="vgp-overlay-settings-content"
            disableCloseButton
          >
            <Captions />
          </Overlay>
        )}
        {openOverlayName === 'next-up' && (
          <Overlay
            onCloseClick={handleOverlayCloseClick}
            disableCloseButton={isMobile && asset && isEpisode(asset)}
          >
            <NextUpSlider onAssetClick={handleNextUpAssetClick} />
          </Overlay>
        )}

        {openOverlayName === 'quality' && (
          <Overlay
            onCloseClick={handleOverlayCloseClick}
            className="vgp-overlay-settings"
            contentOverlayClassName="vgp-overlay-settings-content"
            disableCloseButton
          >
            <Quality />
          </Overlay>
        )}
      </Backdrop>
    </div>
  );
};
